import React from "react";
import { IconButton, Paper, Tooltip } from "@mui/material";
import { FmdGoodOutlined, PentagonOutlined, RectangleOutlined, RestartAltOutlined, ShowChartOutlined } from "@mui/icons-material";
import { DrawMode } from "./CmTableDrawMap";
import { CmTableSpatialTypeRaw, normaliseSpatialType } from "../../services/cmTables";

interface DrawingToolsProps {
  spatialType: CmTableSpatialTypeRaw;
  /** When set (multi-geometry forms), render a tool per allowed kind instead of the single `spatialType`. */
  allowedTypes?: Array<"point" | "linestring" | "polygon">;
  activeMode: DrawMode;
  onModeChange: (mode: DrawMode) => void;
  onReset: () => void;
  /**
   * When a geometry is already drawn, lock the toolbar to its kind: only that
   * kind's tool (and Reset) stay enabled; the others are disabled (not hidden).
   */
  lockedKind?: "point" | "linestring" | "polygon" | null;
}

const BUTTON_SX = (active: boolean) => ({
  color: active ? "primary.main" : "rgba(255,255,255,0.85)",
  p: "0.4rem",
  borderRadius: "0.375rem",
  "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }
});

const DrawingTools: React.FC<DrawingToolsProps> = ({ spatialType, allowedTypes, activeMode, onModeChange, onReset, lockedKind }) => {
  const base = normaliseSpatialType(spatialType);
  // Multi-geometry: show a tool per allowed kind; otherwise fall back to the single spatial type.
  const kinds = allowedTypes && allowedTypes.length > 0 ? allowedTypes : base === "none" ? [] : [base];
  const isPoint = kinds.includes("point");
  const isPolygon = kinds.includes("polygon");
  const isLine = kinds.includes("linestring");

  if (kinds.length === 0) return null;

  const toggle = (mode: DrawMode) => onModeChange(activeMode === mode ? null : mode);
  // A drawn geometry locks the toolbar to its kind — other kinds' tools are disabled until Reset.
  const disabledFor = (kind: "point" | "linestring" | "polygon") => !!lockedKind && lockedKind !== kind;

  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "0.25rem",
        px: "0.5rem",
        py: "0.375rem",
        borderRadius: "0.75rem",
        bgcolor: "#1D2939",
        border: "1px solid rgba(252, 252, 253, 0.15)"
      }}
    >
      {isPoint && (
        <Tooltip title='Place point' placement='top'>
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size='small'
              disabled={disabledFor("point")}
              onClick={() => toggle("point")}
              sx={BUTTON_SX(activeMode === "point")}
              aria-label='Place point'
            >
              <FmdGoodOutlined fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {isPolygon && (
        <>
          <Tooltip title='Draw polygon' placement='top'>
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size='small'
                disabled={disabledFor("polygon")}
                onClick={() => toggle("polygon")}
                sx={BUTTON_SX(activeMode === "polygon")}
                aria-label='Draw polygon'
              >
                <PentagonOutlined fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title='Draw rectangle' placement='top'>
            <span style={{ display: "inline-flex" }}>
              <IconButton
                size='small'
                disabled={disabledFor("polygon")}
                onClick={() => toggle("rectangle")}
                sx={BUTTON_SX(activeMode === "rectangle")}
                aria-label='Draw rectangle'
              >
                <RectangleOutlined fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      {isLine && (
        <Tooltip title='Draw line' placement='top'>
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size='small'
              disabled={disabledFor("linestring")}
              onClick={() => toggle("line")}
              sx={BUTTON_SX(activeMode === "line")}
              aria-label='Draw line'
            >
              <ShowChartOutlined fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Select & vertex-edit is now automatic — clicking a drawn feature selects it */}

      <Tooltip title='Reset drawing' placement='top'>
        <IconButton size='small' onClick={onReset} sx={BUTTON_SX(false)} aria-label='Reset drawing'>
          <RestartAltOutlined fontSize='small' />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default DrawingTools;
